/**
 * Superadmin read/write of this node's identity settings — legal_entity_name
 * (Consents tab, Account/Consents.jsx) and logo_url (Branding tab,
 * Account/NodeBranding.jsx).
 *
 * Reads GET /api/node-settings/public (no auth needed to read; writes are
 * gated superadmin-only on the backend). Saving invalidates the cached
 * public value (use_node_settings.js) so the Terms/Privacy pages, footer,
 * and navbar pick up the new value without a full reload.
 *
 *   const { legalEntityName, logoUrl, loading, update, uploadLogo, removeLogo } = useNodeSettingsAdmin();
 */

import { useState, useEffect, useCallback } from 'react';
import { useApi } from './use_api';
import { useAuth } from '../context/AuthContext';
import { invalidateNodeSettings } from './use_node_settings';

export function useNodeSettingsAdmin() {
    const api = useApi();
    const { user } = useAuth();
    const userId = user?.id;

    const [legalEntityName, setLegalEntityName] = useState('');
    const [logoUrl, setLogoUrl] = useState(null);
    const [avatar, setAvatar] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get('/api/node-settings/public')
            .then(data => {
                setLegalEntityName(data?.legal_entity_name || '');
                setLogoUrl(data?.logo_url || null);
                setAvatar(data?.avatar || null);
            })
            .finally(() => setLoading(false));
    }, [api]);

    const update = useCallback(name => api.put(
        '/api/node-settings', { legal_entity_name: name }, { params: { user_id: userId } },
    ).then(data => {
        invalidateNodeSettings();
        setLegalEntityName(data?.legal_entity_name || '');
        return data;
    }), [api, userId]);

    const uploadLogo = useCallback(file => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/api/node-settings/logo', formData, { params: { user_id: userId } })
            .then(data => {
                invalidateNodeSettings();
                setLogoUrl(data?.logo_url || null);
                return data;
            });
    }, [api, userId]);

    // Reshuffle the generated avatar: { seed, colors }, or null for the defaults.
    const updateAvatar = useCallback(spec => api.put(
        '/api/node-settings/avatar', { avatar: spec }, { params: { user_id: userId } },
    ).then(data => {
        invalidateNodeSettings();
        setAvatar(data?.avatar || null);
        return data;
    }), [api, userId]);

    const removeLogo = useCallback(() => api.del(
        '/api/node-settings/logo', { params: { user_id: userId } },
    ).then(data => {
        invalidateNodeSettings();
        setLogoUrl(null);
        return data;
    }), [api, userId]);

    return { legalEntityName, logoUrl, avatar, loading, update, uploadLogo, removeLogo, updateAvatar };
}

export default useNodeSettingsAdmin;
